/**
 * Reads for the seller desk (canon APP-002).
 *
 * Two disciplines apply to everything in this file.
 *
 * **The session decides whose data this is.** Neither request carries a seller id, a deal
 * id or any other identifier — there is no parameter for a caller to vary, which is how
 * `docs/ARCHITECTURE.md` § Seller app's "never trust seller/deal IDs from the browser"
 * is met here: not by checking an id, but by having none to send.
 *
 * **A response body is untrusted input.** A browser can be pointed at any host, so every
 * field is parsed rather than cast, using the primitives in `./parse`. The parsers return
 * `null` for a body that does not match the contract, and the caller turns that into a
 * visible failure. That is deliberate: a desk that quietly rendered a half-understood
 * payload would be showing a founder the wrong review state or the wrong balance, which is
 * worse than showing nothing.
 */
import { apiRequest } from "./client";
import {
  INVALID,
  malformed,
  nonEmptyString,
  optionalCount,
  optionalString,
  requiredCount,
} from "./parse";
import type { Parsed } from "./parse";
import type {
  BadgeCode,
  DealType,
  SellerCredit,
  SignupGrant,
  SellerDeal,
  SellerDealStatus,
} from "./types";

const BADGE_CODES = new Set<string>(["founding", "ltd", "exclusive"]);
const DEAL_TYPES = new Set<string>([
  "lifetime",
  "discount",
  "credits",
  "founder_offer",
  "other",
]);
const DEAL_STATUSES = new Set<string>([
  "submitted",
  "changes_required",
  "approved",
  "rejected",
  "disabled",
]);

function isDealType(value: string): value is DealType {
  return DEAL_TYPES.has(value);
}

/**
 * An unrecognised review status fails the parse instead of degrading to a default.
 *
 * Core does degrade an unexpected column value to `submitted` (its schema CHECK makes that
 * unreachable in practice), but doing the same here would mean this panel — whose entire
 * job is to tell a founder where their deal stands — could state a status core never sent.
 * Failing loudly is the honest direction to fail.
 */
function isDealStatus(value: string): value is SellerDealStatus {
  return DEAL_STATUSES.has(value);
}

/**
 * Unknown badge codes are dropped rather than rejected. Canon § 4 may gain a badge before
 * this app learns to draw it, and showing the badges we understand is the safe direction to
 * fail — unlike a review status, a missing badge misstates nothing.
 */
function parseBadges(value: unknown): Parsed<BadgeCode[]> {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return INVALID;
  return value.filter(
    (code): code is BadgeCode => typeof code === "string" && BADGE_CODES.has(code),
  );
}

export function parseSellerDeal(value: unknown): SellerDeal | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;

  const id = nonEmptyString(row.id);
  const slug = nonEmptyString(row.slug);
  const productName = nonEmptyString(row.productName);
  const submittedAt = nonEmptyString(row.submittedAt);
  const status = nonEmptyString(row.status);

  // Identity and review state are the reason this panel exists. A deal missing any of them
  // cannot be rendered honestly, so it is refused rather than shown with a guessed status.
  if (!id || !slug || !productName || !submittedAt || !status) return null;
  if (!isDealStatus(status)) return null;

  const dealType = optionalString(row.dealType);
  const badges = parseBadges(row.badges);
  if (dealType === INVALID || badges === INVALID) return null;
  if (dealType !== null && !isDealType(dealType)) return null;

  const productUrl = optionalString(row.productUrl);
  const logoUrl = optionalString(row.logoUrl);
  const shortDescription = optionalString(row.shortDescription);
  const dealUrl = optionalString(row.dealUrl);
  const valueNotes = optionalString(row.valueNotes);
  const couponCode = optionalString(row.couponCode);
  const limitations = optionalString(row.limitations);
  const verifiedAt = optionalString(row.verifiedAt);
  const approvedAt = optionalString(row.approvedAt);
  // D-016: what Dealbid is currently asking for. Optional rather than required — a deal
  // moderated before core recorded these reads `null`, and refusing the whole deal over an
  // absent note would take the founder's dashboard away to report that nothing was said.
  const outstandingNote = optionalString(row.outstandingNote);
  if (
    outstandingNote === INVALID ||
    productUrl === INVALID ||
    logoUrl === INVALID ||
    shortDescription === INVALID ||
    dealUrl === INVALID ||
    valueNotes === INVALID ||
    couponCode === INVALID ||
    limitations === INVALID ||
    verifiedAt === INVALID ||
    approvedAt === INVALID
  ) {
    return null;
  }

  const originalPriceCents = optionalCount(row.originalPriceCents);
  const dealPriceCents = optionalCount(row.dealPriceCents);
  const discountPercent = optionalCount(row.discountPercent);
  if (
    originalPriceCents === INVALID ||
    dealPriceCents === INVALID ||
    discountPercent === INVALID
  ) {
    return null;
  }

  return {
    id,
    slug,
    productName,
    productUrl,
    logoUrl,
    shortDescription,
    dealUrl,
    dealType,
    originalPriceCents,
    dealPriceCents,
    discountPercent,
    valueNotes,
    couponCode,
    limitations,
    badges,
    // Anything other than a literal `true` is not a verification claim. `Deal Verified` is
    // a canon trust signal (§ 4), so it is never inferred from a truthy value.
    verified: row.verified === true,
    verifiedAt,
    status,
    isPublished: row.isPublished === true,
    submittedAt,
    approvedAt,
    outstandingNote,
  };
}

/**
 * Returns `null` when the body is not a deal list.
 *
 * One malformed deal fails the whole list rather than being skipped. A founder with one
 * deal who is shown an empty desk would reasonably conclude Dealbid lost it; "this didn't
 * load" is the true statement, and it is the one that gets reported.
 */
export function parseSellerDeals(body: unknown): SellerDeal[] | null {
  if (typeof body !== "object" || body === null) return null;
  const deals = (body as { deals?: unknown }).deals;
  if (!Array.isArray(deals)) return null;

  const parsed: SellerDeal[] = [];
  for (const entry of deals) {
    const deal = parseSellerDeal(entry);
    if (deal === null) return null;
    parsed.push(deal);
  }
  return parsed;
}

/**
 * Returns `null` when the body is not a credit position.
 *
 * `terms` is required, not optional. Canon `AGENTS.md` § 7 forbids presenting promotional
 * credit as withdrawable money, and `contracts/API.md` ships the non-cash sentence with the
 * numbers so that no surface can show one without the other. A body that carries a balance
 * and no terms is therefore not a body this app may render — refusing it is what keeps that
 * guarantee from degrading into a bare number on screen.
 */
export function parseSellerCredit(body: unknown): SellerCredit | null {
  if (typeof body !== "object" || body === null) return null;
  const credit = (body as { credit?: unknown }).credit;
  if (typeof credit !== "object" || credit === null) return null;

  const row = credit as Record<string, unknown>;
  const currency = nonEmptyString(row.currency);
  const availableCents = requiredCount(row.availableCents);
  const reservedCents = requiredCount(row.reservedCents);
  const consumedCents = requiredCount(row.consumedCents);
  if (
    !currency ||
    availableCents === INVALID ||
    reservedCents === INVALID ||
    consumedCents === INVALID
  ) {
    return null;
  }

  const terms = row.terms;
  if (typeof terms !== "object" || terms === null) return null;
  const code = nonEmptyString((terms as Record<string, unknown>).code);
  const message = nonEmptyString((terms as Record<string, unknown>).message);
  if (!code || !message) return null;

  const signupGrant = parseSignupGrant(row.signupGrant);
  if (signupGrant === null) return null;

  return {
    currency,
    availableCents,
    reservedCents,
    consumedCents,
    signupGrant,
    terms: { code, message },
  };
}

/**
 * Canon § 5's grant band (decision `D-011`).
 *
 * Required, and the null band is a *value* rather than a missing field: "no deal approved
 * yet" and "approved into the $0 founding cohort" are different facts that a dashboard has
 * to say differently, so neither may be inferred from an absent object.
 */
function parseSignupGrant(value: unknown): SignupGrant | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;

  const band =
    row.band === "founding" || row.band === "growth" || row.band === "standard"
      ? row.band
      : row.band === null
        ? null
        : INVALID;
  if (band === INVALID) return null;

  // The two travel together: a band always has an amount, and no band never has one.
  if (band === null) return row.cents === null ? { band: null, cents: null } : null;

  const cents = requiredCount(row.cents);
  if (cents === INVALID) return null;
  return { band, cents };
}

/** Every deal belonging to the signed-in seller, at every review stage. */
export async function fetchSellerDeals(): Promise<SellerDeal[]> {
  const deals = parseSellerDeals(await apiRequest<unknown>("/v1/seller/deals"));
  if (deals === null) throw malformed();
  return deals;
}

/** The signed-in seller's promotional credit position. */
export async function fetchSellerCredit(): Promise<SellerCredit> {
  const credit = parseSellerCredit(await apiRequest<unknown>("/v1/seller/credit"));
  if (credit === null) throw malformed();
  return credit;
}
